/* Where a rule stands, and when a binding runs — the two readings the Rules screen is built on.
 *
 * **Five phases where the API has three states**, because the design draws the journey a reader has
 * to pass through rather than the column a row holds. `draft`, `active` and `deprecated` are rule
 * states; `published` and `awaiting` are gates inside `draft` — a version has been frozen, and then a
 * request is waiting on signatures. Both are observable from `GET /v1/rules/overview`, which is the
 * one read that carries a rule's latest version and its activation request together.
 *
 * Kept here rather than in the component, because the strip's counts, the row's pill and the
 * drawer's stepper must agree about which phase a rule is in, and three components each deciding is
 * how two of them come to disagree.
 */

import type {
  RuleOverview,
  RuleState,
  SchedulingSummary,
} from '@/shared/api/generated/rules';

export type RulePhase = 'draft' | 'published' | 'awaiting' | 'active' | 'deprecated';

/** A request still counts as asking for activation while it is pending, approved but unspent, or
 * escalated. `rejected` closes it — the author asks again — and `consumed` means it was spent on the
 * activation, which a draft cannot be showing. */
const LIVE_REQUEST_STATES: ReadonlySet<string> = new Set([
  'pending',
  'approved',
  'escalated',
]);

/** Whether a request is still asking — the one test both callers below share. */
export function isLiveRequest(request: { readonly state: string } | null): boolean {
  return request !== null && LIVE_REQUEST_STATES.has(request.state);
}

/** The phase from its three ingredients, for the drawer, which reads them one rule at a time and may
 * be showing a rule the overview page does not hold. */
export function phaseFrom(
  state: RuleState,
  hasVersion: boolean,
  activation: { readonly state: string } | null,
): RulePhase {
  if (state === 'active') return 'active';
  if (state === 'deprecated') return 'deprecated';
  if (isLiveRequest(activation)) return 'awaiting';
  return hasVersion ? 'published' : 'draft';
}

export function phaseOf(row: RuleOverview): RulePhase {
  return phaseFrom(row.rule.state, row.latest_version !== null, row.activation);
}

export interface PhaseStep {
  readonly phase: RulePhase;
  /** A gate is passed through rather than held, and the strip draws it dashed. */
  readonly gate: boolean;
}

/** The strip, in order. The order is the journey, so it is a list rather than a lookup. */
export const PHASES: readonly PhaseStep[] = [
  { phase: 'draft', gate: false },
  { phase: 'published', gate: true },
  { phase: 'awaiting', gate: true },
  { phase: 'active', gate: false },
  { phase: 'deprecated', gate: false },
];

export type PhaseTone = 'muted' | 'info' | 'lav' | 'ok';

/** The design's colours for the phases: a draft and a deprecated rule are quiet, a published one
 * has something frozen, one waiting on signatures wears the lavender of "waiting on a person", and
 * only an active rule is settled. Total over the union, so a sixth phase is a compile error here. */
export const PHASE_TONE = {
  draft: 'muted',
  published: 'info',
  awaiting: 'lav',
  active: 'ok',
  deprecated: 'muted',
} as const satisfies Record<RulePhase, PhaseTone>;

/** What the Approval column says, or nothing — a rule not waiting on anybody has no approval to
 * report, and a blank cell says that better than a word would. */
export type ApprovalReading =
  | { readonly kind: 'none' }
  | { readonly kind: 'collecting'; readonly collected: number; readonly required: number }
  | { readonly kind: 'ready' };

export function approvalOf(row: RuleOverview): ApprovalReading {
  if (phaseOf(row) !== 'awaiting' || row.activation === null) return { kind: 'none' };
  return row.activation.satisfied
    ? { kind: 'ready' }
    : {
        kind: 'collecting',
        collected: row.activation.eyes_collected,
        required: row.activation.required_eyes,
      };
}

/* ------------------------------------------------------------------ when a binding runs */

export type RunFilter = 'all' | 'manual' | 'scheduled' | 'after_harvest';

export const RUN_FILTERS: readonly RunFilter[] = [
  'all',
  'manual',
  'scheduled',
  'after_harvest',
];

/** Which unattended triggers are switched on for a binding, or `null` when its scheduling has not
 * been read. A binding can be both scheduled and harvest-driven, so these are two flags rather than
 * one mode — and `null` is not "manual": an unread answer is not an absence of schedules. */
export interface RunModes {
  readonly scheduled: boolean;
  readonly afterHarvest: boolean;
}

export function runModesOf(summary: SchedulingSummary | undefined): RunModes | null {
  if (summary === undefined) return null;
  return {
    scheduled: summary.schedule.configured && summary.schedule.enabled,
    afterHarvest: summary.trigger.configured && summary.trigger.enabled,
  };
}

/** Whether a binding belongs under a filter. An unread binding is shown only under "All": placing it
 * under "Manual" would claim it has no schedule, which nobody has checked. */
export function matchesRunFilter(modes: RunModes | null, filter: RunFilter): boolean {
  if (filter === 'all') return true;
  if (modes === null) return false;
  if (filter === 'scheduled') return modes.scheduled;
  if (filter === 'after_harvest') return modes.afterHarvest;
  return !modes.scheduled && !modes.afterHarvest;
}
